import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useDeleteFlow } from '../../components/confirm';
import { BusyIcon, LoaderSymbol, runLoading } from '../../components/loading';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { fmtLocalDate } from '../../utils/datetime';
import { saveBlobAs } from '../../utils/exportData';
import { StepHint } from '../../components/StepHint/StepHint';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { FormTitle } from '../../components/FormTitle';
import { FormMetaChips } from '../../components/AppToolbar';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { mergeMsrRows, pruneMismatchedMsrRows } from './msrMerge';
import './SettingsDetailPage.css';   // inline mode reuses the Settings-detail `.sd-*` chrome

/**
 * Monthly Summary Repository — New / Edit detail dialog (ALPACA-LIMS parity).
 * Year / Month / Category pickers, a **Retrieve** that pulls the month's samples
 * for the chosen category (server `/retrieve`, filtered by its Data Import Config)
 * and fills the field-results grid, **Reset** to clear the grid, and
 * **Save** to mint the MSRID + persist the header and the sample rows.
 *
 * Replaces the generic detail view for the `msr` module (ModuleListView
 * special-cases it — the Retrieve/samples grid can't be expressed as a
 * MODULE_CRUD field list).  Pull/Save use the same
 * /api/v1/monthly-summary-repository endpoints; the minted MSRID is race-safe
 * (server side, §14a Rule B).
 * (Merged team package 2026-07-02; conformed to the shared `.ca-btn` /
 * `useSaveFlow` / `useNotify` / `<BusyIcon>` / token standard — CLAUDE.md §18.6.)
 */
interface Props {
  /** null = New; an Oid = Edit (header prefilled; grid re-retrievable). */
  id: string | null;
  onClose: () => void;
  onSaved: () => void;
  /**
   * Render INLINE as a full-page detail (Settings-style: header + breadcrumb +
   * body + footer inside `.bl-page`/`.sd-wrap`) instead of the `.qs-overlay`
   * popup.  Used by ModuleListView so MSR's New/Edit opens the full-page
   * List/Detail tab, not a modal (2026-07-09).  Same fields / Retrieve / Save /
   * Cancel behaviour in both modes.
   */
  inline?: boolean;
  /** Breadcrumb group (inline mode) — the module's nav group. */
  group?: string;
  /** Form title (inline mode) — the nav label; used in the header + breadcrumb. */
  title?: string;
  /** Home breadcrumb link (inline mode). */
  onHome?: () => void;
  /** Open a blank New MSR — wired to ModuleListView.openNew (inline mode). */
  onNew?: () => void;
}

interface CategoryOption { id: string; name: string; excelImport?: boolean }

interface SampleRow {
  sampleLogInOid: string;
  projectId?: string; siteName?: string; address?: string; date?: string; time?: string; keyMap?: string;
  freeChlorine?: string; totalChlorine?: string; monochloramine?: string; freeAmmonia?: string;
  totalColiform?: string; ecoli?: string; nitrite?: string; nitrate?: string;
  disinfectant?: string; collector?: string; comment?: string; sampleType?: string;
  jobId?: string; sampleId?: string;
  /** Client-only flag (never sent to / received from the API): true for a row
   *  newly discovered by Load/Retrieve that isn't yet part of the saved
   *  snapshot — ALPACA-LIMS parity for MonthlySummaryRepositorySamples.IsNew
   *  (green row, see module.css .msr-row-new). Cleared once the record is
   *  reloaded from the server (a saved row is never "new"). */
  isNew?: boolean;
  /** Client-only flag: a saved row whose values the latest Load changed —
   *  ALPACA-LIMS parity for MonthlySummaryRepositorySamples.IsModified (orange
   *  row, see module.css .msr-row-modified). Never sent to the API. */
  isModified?: boolean;
}

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// GoLive's exact 17-column export layout: the date header carries a TRAILING
// SPACE and the last column is SampleCollector — the client's program that
// consumes this file matches that layout (fix 2026-08-13: AI exported only 16
// columns with no SampleCollector, which crashed the client program).
const MSR_EXPORT_HEADERS = [
  'Run', 'SiteID', 'Address', 'KeyMap', 'Sample Collected Date & Time ',
  'FreeChlorine (mg/L)', 'TotalChlorine (mg/L)', 'MonoChloramine (mg/L Cl2)',
  'FreeAmmonia (mg/L NH3-N)', 'TotalColiform', 'E.coli',
  'Nitrite (mg/L)', 'Nitrate (mg/L)', 'SampleType', 'Disinfectant', 'Comments', 'SampleCollector',
];

// GoLive column widths — one entry per header (17).
const MSR_COL_WIDTHS = [6.29, 15.29, 34.43, 8.29, 28.29, 18.86, 19.29, 26, 26.14, 13.14, 11.86, 13.14, 13.57, 11.86, 11.71, 10.57, 16];

// GoLive "Sample Collected Date & Time" format: MM/dd/yyyy hh:mm tt — zero-padded
// 12-hour + AM/PM (e.g. "08/04/2026 09:33 AM"). EXTERNAL machine-read export → matches
// GoLive EXACTLY, not the app-wide 24-hour UI rule (fix 2026-08-13: AI emitted
// "yyyy/MM/dd HH:mm", which the client program could not parse).
function msrFormatDateTime(date?: string, time?: string): string {
  if (!date) return '';
  const d = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  const datePart = d ? `${d[2]}/${d[3]}/${d[1]}` : date;
  const t = time ? /^(\d{1,2}):(\d{2})/.exec(time) : null;
  let timePart = '';
  if (t) {
    const h = parseInt(t[1] ?? '0', 10);
    const ampm = h < 12 ? 'AM' : 'PM';
    const h12 = (h % 12) === 0 ? 12 : h % 12;
    timePart = `${String(h12).padStart(2, '0')}:${t[2] ?? ''} ${ampm}`;
  }
  return [datePart, timePart].filter(Boolean).join(' ');
}

function sampleToExportRow(s: SampleRow): string[] {
  return [
    s.projectId ?? '', s.siteName ?? '', s.address ?? '', s.keyMap ?? '',
    msrFormatDateTime(s.date, s.time),
    s.freeChlorine ?? '', s.totalChlorine ?? '', s.monochloramine ?? '',
    s.freeAmmonia ?? '', s.totalColiform ?? '', s.ecoli ?? '',
    s.nitrite ?? '', s.nitrate ?? '', s.sampleType ?? '', s.disinfectant ?? '', s.comment ?? '',
    s.collector ?? '',
  ];
}

function msrTimestamp(): string {
  const n = new Date();
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${n.getFullYear()}${pad(n.getMonth() + 1)}${pad(n.getDate())}${pad(n.getHours())}${pad(n.getMinutes())}${pad(n.getSeconds())}`;
}

async function exportMsrXlsx(rows: SampleRow[], filename: string): Promise<{ blob: Blob; name: string }> {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Data');

  // Column widths — GoLive parity
  ws.columns = MSR_COL_WIDTHS.map((w, i) => ({ header: MSR_EXPORT_HEADERS[i], width: w }));

  // Header row — bold, font size 11, Calibri (GoLive parity)
  const headerRow = ws.getRow(1);
  headerRow.font = { name: 'Calibri', size: 11, bold: true };
  headerRow.commit();

  // Data rows — font size 11, Calibri
  for (const s of rows) {
    const row = ws.addRow(sampleToExportRow(s));
    row.font = { name: 'Calibri', size: 11 };
    row.commit();
  }

  // Freeze first row and first column — GoLive parity
  ws.views = [{ state: 'frozen', xSplit: 1, ySplit: 1 }];

  // Return the built file — the CALLER saves it (via saveBlobAs) once the
  // blocking loader is gone, so the "File Ready to Save" dialog is clickable.
  const buf = await wb.xlsx.writeBuffer();
  return {
    blob: new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    name: `${filename}.xlsx`,
  };
}

function exportMsrCsv(rows: SampleRow[], filename: string): { blob: Blob; name: string } {
  const csvCell = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const csv = [MSR_EXPORT_HEADERS, ...rows.map(sampleToExportRow)]
    .map((r) => r.map(csvCell).join(',')).join('\r\n');
  return {
    blob: new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }),
    name: `${filename}.csv`,
  };
}

// Field-result columns (the chemistry set + free-text).
// Order matches ALPACA-LIMS MSR grid: chemistry → SampleType → Disinfectant → Comment.
const RESULT_COLS: { key: keyof SampleRow; label: string }[] = [
  { key: 'freeChlorine',   label: 'FreeChlorine' },
  { key: 'totalChlorine',  label: 'TotalChlorine' },
  { key: 'monochloramine', label: 'MonoChloramine' },
  { key: 'freeAmmonia',    label: 'FreeAmmonia' },
  { key: 'totalColiform',  label: 'TotalColiform' },
  { key: 'ecoli',          label: 'E.coli' },
  { key: 'nitrite',        label: 'Nitrite' },
  { key: 'nitrate',        label: 'Nitrate' },
  { key: 'sampleType',     label: 'SampleType' },
  { key: 'disinfectant',   label: 'Disinfectant' },
  { key: 'comment',        label: 'Comment' },
];

// Shared-grid columns (§9) — matches ALPACA-LIMS MSR column order exactly:
// Run · SiteID · KeyMap · Address · Date · Time · [chemistry] · SampleType ·
// Disinfectant · Comment · SampleCollector · JobID · SampleID.
const GRID_COLS: AppColumn<SampleRow>[] = [
  { field: 'projectId', header: 'Run' },
  { field: 'siteName',  header: 'SiteID' },
  { field: 'keyMap',    header: 'KeyMap' },
  { field: 'address',   header: 'Address' },
  { field: 'date',      header: 'Date', render: (r) => fmtLocalDate(r.date), text: (r) => fmtLocalDate(r.date) },
  { field: 'time',      header: 'Time' },
  ...RESULT_COLS.map((c): AppColumn<SampleRow> => ({
    field: c.key as string, header: c.label, minWidth: c.key === 'comment' ? 120 : 70,
  })),
  { field: 'collector', header: 'SampleCollector' },
  { field: 'jobId',     header: 'JobID' },
  { field: 'sampleId',  header: 'SampleID' },
];

export function MsrDetailDialog({ id, onClose, onSaved, inline, group, title, onHome, onNew }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const editing = id != null;
  const now = new Date();
  // AfterConstruction parity: the legacy `(Month)(Today.Month - 1)` is a
  // 1-based→0-based conversion that lands on the CURRENT month; Year = current.
  const [year, setYear]     = useState(String(now.getFullYear()));
  const [month, setMonth]   = useState(String(now.getMonth()));
  const [category, setCategory] = useState('');
  const [cats, setCats]     = useState<CategoryOption[]>([]);
  const [samples, setSamples] = useState<SampleRow[]>([]);
  const [msrId, setMsrId]   = useState('');
  // Read-only audit metadata (shown on the detail's right column — legacy parity).
  const [createdDate, setCreatedDate]         = useState('');
  const [createdBy, setCreatedBy]             = useState('');
  const [lastUpdatedDate, setLastUpdatedDate] = useState('');
  const [lastUpdatedBy, setLastUpdatedBy]     = useState('');
  const [loading, setLoading] = useState(editing);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [retrieving, setRetrieving] = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedSamples, setSelectedSamples] = useState<Set<string | number>>(new Set());
  const [importFile, setImportFile] = useState<File | null>(null);
  // A saved record opens read-only; the sub-toolbar Edit button flips this on to
  // make the form editable (Viewing → Edit, user request 2026-07-15).
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get<{ data?: CategoryOption[] }>('/api/v1/monthly-summary-repository/categories')
      .then((r) => { if (alive) setCats(r.data.data ?? []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Edit: prefill the header AND load the already-saved samples into the grid
  // immediately (no Retrieve) — ALPACA-LIMS parity.
  useEffect(() => {
    if (!editing) return;
    let alive = true;
    setLoading(true);
    setEditMode(false);   // a (re)load returns to the read-only Viewing state
    const t0 = performance.now();
    Promise.all([
      api.get<{ data?: Record<string, unknown> }>(`/api/v1/monthly-summary-repository/${id}`),
      api.get<{ data?: SampleRow[] }>(`/api/v1/monthly-summary-repository/${id}/samples`),
    ])
      .then(([hdr, smp]) => {
        if (!alive) return;
        const d = hdr.data.data ?? {};
        setMsrId(String(d.msrId ?? ''));
        setCreatedDate(String(d.createdDate ?? ''));
        setCreatedBy(String(d.createdBy ?? ''));
        setLastUpdatedDate(String(d.lastUpdatedDate ?? ''));
        setLastUpdatedBy(String(d.lastUpdatedBy ?? ''));
        // The category dropdown's option values are lowercase (JSON-serialised
        // Guids); SQL CONVERT returns the saved Oid UPPERCASE — lowercase it so the
        // <select> matches and shows the saved category instead of the placeholder.
        setCategory(String(d.category ?? '').toLowerCase());
        if (d.month != null && d.month !== '') setMonth(String(d.month));
        setYear(String(d.year ?? ''));
        setSamples(smp.data.data ?? []);
      })
      .catch(() => { if (alive) notify.error('Could not load this Monthly Summary.'); })
      .finally(() => { if (alive) { setLoading(false); setLoadMs(performance.now() - t0); } });
    return () => { alive = false; };
  }, [editing, id, notify, refreshKey]);

  const valid = () => {
    if (!category) { notify.warning('Please select a category.'); return false; }
    if (!year)     { notify.warning('Please select year.'); return false; }
    if (month === '') { notify.warning('Please select month.'); return false; }
    return true;
  };

  const retrieve = async () => {
    if (!valid()) return;
    setRetrieving(true);
    try {
      let rows: SampleRow[];

      // Excel import path — when the category has ExcelImport=true AND a file is chosen.
      if (selectedCategory?.excelImport && importFile) {
        const fd = new FormData();
        fd.append('file', importFile);
        fd.append('categoryName', selectedCategory.name);
        fd.append('month', month);
        fd.append('year', year);
        const res = await api.post<{ data?: SampleRow[] }>(
          '/api/v1/monthly-summary-repository/retrieve-excel', fd,
          { headers: { 'Content-Type': 'multipart/form-data' } });
        rows = res.data.data ?? [];
      } else {
        // Standard DB-retrieve path.
        const res = await api.post<{ data?: SampleRow[] }>(
          '/api/v1/monthly-summary-repository/retrieve',
          // On a NEW MSR, refuse if one already exists for this Category/Month/Year
          // ("The record already exists, process denied." — legacy parity).
          { category, month: Number(month), year, checkDuplicate: !editing });
        rows = res.data.data ?? [];
      }

      // Merge, don't replace — ALPACA-LIMS parity (see mergeMsrRows above).
      const kept = pruneMismatchedMsrRows(samples, year, month, selectedCategory?.name ?? '');
      const merged = mergeMsrRows(kept, rows);
      const addedCount = merged.length - kept.length;
      setSamples(merged);
      setSelectedSamples(new Set());
      notify.success(addedCount > 0
        ? `Retrieved ${addedCount} new sample${addedCount > 1 ? 's' : ''}.`
        : rows.length > 0
          ? 'No new samples — everything shown was already retrieved.'
          : 'No samples found for this selection.');
    } catch (e) {
      notify.error((e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Retrieve failed.');
    } finally { setRetrieving(false); }
  };

  // Reset (New only) — clears the picks back to defaults + empties the grid,
  // like the legacy ResetMSRAction.  A Saved MSR can't be reset (button disabled).
  const reset = () => {
    setCategory('');
    setMonth(String(now.getMonth()));
    setYear(String(now.getFullYear()));
    setSamples([]);
    setSelectedSamples(new Set());
    setImportFile(null);
  };

  // The currently selected category object (for ExcelImport flag access).
  const selectedCategory = cats.find((c) => c.id === category);

  // Build the file under the blocking loader, THEN save it once the loader is
  // gone — the scrim would otherwise sit on top of the "File Ready to Save"
  // dialog and the spinner would hang forever (fix 2026-08-14).
  const exportSamples = async (kind: 'xlsx' | 'csv') => {
    const rows = selectedSamples.size > 0
      ? samples.filter((s, i) => selectedSamples.has(s.sampleLogInOid || i))
      : samples;
    if (rows.length === 0) { notify.info('No samples to export.'); return; }
    const filename = `MSRSamples_${msrTimestamp()}`;
    let built: { blob: Blob; name: string };
    try {
      built = await runLoading(
        kind === 'xlsx' ? exportMsrXlsx(rows, filename) : Promise.resolve(exportMsrCsv(rows, filename)),
        { blocking: true, label: `Exporting to ${kind.toUpperCase()}…` });
    } catch {
      notify.error(`Export to ${kind.toUpperCase()} failed.`);
      return;
    }
    try {
      if (!await saveBlobAs(built.blob, built.name)) return;   // cancelled → no toast
      notify.success(`Exported to ${kind.toUpperCase()} successfully.`);
    } catch {
      notify.error(`Export to ${kind.toUpperCase()} failed.`);
    }
  };

  // Save via the ONE shared save flow (§18.6): "Do you want to Save?" →
  // POST/PUT (server audits + mints MSRID) → "Saved successfully." → back to
  // the list.  Edit gates the audit reason (standing rule 11); Create is exempt.
  const save = async () => {
    if (!valid()) return;
    await runSave({
      formName: 'Monthly Summary Repository',
      auditAction: editing ? 'edit' : 'create',
      save: async () => {
        // Strip the client-only isNew / isModified flags (row colours) before persisting.
        const body = { category, month: String(month), year, samples: samples.map(({ isNew, isModified, ...s }) => s) };
        // Edit → PUT the existing MSR (header + replace samples); New → POST (mint MSRID).
        if (editing) await api.put(`/api/v1/monthly-summary-repository/${id}`, body);
        else await api.post('/api/v1/monthly-summary-repository', body);
      },
      onSaved,
    });
  };

  const deleteRecord = async () => {
    if (!editing || !id) return;
    setDeleting(true);
    try {
      await runDelete({
        formName: 'Monthly Summary Repository',
        message: `Delete ${msrId || 'this Monthly Summary'}?`,
        delete: async () => {
          await api.delete(`/api/v1/monthly-summary-repository/${id}`);
        },
        onDeleted: onSaved,
      });
    } finally { setDeleting(false); }
  };

  // A saved MSR opens as a read-only snapshot; clicking Edit (sub-toolbar) flips
  // editMode on to allow corrections.  A New record is always editable.
  const viewOnly = editing && !editMode;
  const recordName = editing
    ? (msrId ? `Monthly Summary — ${msrId}` : 'Monthly Summary')
    : 'New Monthly Summary';

  // Body (pickers + Retrieve/Reset + samples grid) — identical in popup & inline.
  const body = (
    loading ? (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--ai-muted)' }}>
        <LoaderSymbol size={22} /> Loading…
      </div>
    ) : (
      <>
        {/* Two-column header (legacy layout parity): LEFT = Year/Month/Category/
            Browse pickers; RIGHT = read-only MSRID + audit metadata (shown for a
            saved record). (user request 2026-07-15) */}
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 8, alignItems: 'center', flex: '1 1 380px', minWidth: 300, maxWidth: 560 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Year:</label>
          <input className="qs-input" value={year} maxLength={4} disabled={viewOnly || editing}
            onChange={(e) => setYear(e.target.value.replace(/[^0-9]/g, ''))} style={{ width: '100%' }} />
          <label style={{ fontSize: 12, fontWeight: 600 }}>Month:</label>
          <select className="qs-input" value={month} disabled={viewOnly || editing} onChange={(e) => setMonth(e.target.value)} style={{ width: '100%' }}>
            {MONTHS.map((m, i) => <option key={i} value={String(i)}>{m}</option>)}
          </select>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Category:</label>
          <select className="qs-input" value={category} disabled={viewOnly || editing} onChange={(e) => { setCategory(e.target.value); setImportFile(null); }} style={{ width: '100%' }}>
            <option value="">Select a category…</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          {/* Browse — only shown (and enabled) when the selected category has
              ExcelImport=true in MSR Data Import Config (GoLive parity). */}
          <label style={{ fontSize: 12, fontWeight: 600, color: (!viewOnly && selectedCategory?.excelImport) ? 'inherit' : 'var(--ai-muted)' }}>
            Browse:
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="file"
              accept=".xlsx,.xls"
              disabled={viewOnly || !selectedCategory?.excelImport}
              style={{ flex: 1, fontSize: 12, opacity: (!viewOnly && selectedCategory?.excelImport) ? 1 : 0.45, cursor: (!viewOnly && selectedCategory?.excelImport) ? 'pointer' : 'not-allowed' }}
              onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
              title={selectedCategory?.excelImport ? 'Select an Excel file to import' : 'Excel import is not enabled for this category'}
            />
            {importFile && (
              <button className="ca-btn" style={{ fontSize: 11, padding: '2px 6px' }}
                onClick={() => setImportFile(null)} title="Clear file">
                ✕
              </button>
            )}
          </div>
        </div>

        {/* RIGHT column — read-only MSRID + audit metadata (saved record only). */}
        {viewOnly && (
          <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 8, alignItems: 'center', flex: '1 1 380px', minWidth: 300, maxWidth: 560 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>MSRID:</label>
            <input className="qs-input" value={msrId} disabled style={{ width: '100%' }} />
            <label style={{ fontSize: 12, fontWeight: 600 }}>Created Date:</label>
            <input className="qs-input" value={createdDate ? fmtLocalDate(createdDate) : ''} disabled style={{ width: '100%' }} />
            <label style={{ fontSize: 12, fontWeight: 600 }}>Created By:</label>
            <input className="qs-input" value={createdBy} disabled style={{ width: '100%' }} />
            <label style={{ fontSize: 12, fontWeight: 600 }}>Last Updated Date:</label>
            <input className="qs-input" value={lastUpdatedDate ? fmtLocalDate(lastUpdatedDate) : ''} disabled style={{ width: '100%' }} />
            <label style={{ fontSize: 12, fontWeight: 600 }}>Last Updated By:</label>
            <input className="qs-input" value={lastUpdatedBy || 'N/A'} disabled style={{ width: '100%' }} />
          </div>
        )}
        </div>

        {/* Export buttons + sample count — Load/Reset live in the action bar above */}
        <div style={{ display: 'flex', gap: 8, margin: '12px 0', flexWrap: 'wrap' }}>
          <button className="ca-btn" disabled={selectedSamples.size === 0}
            title="Export To XLSX"
            onClick={() => void exportSamples('xlsx')}>
            <i className="ti ti-file-type-xls" /> Export To XLSX
          </button>
          <button className="ca-btn" disabled={selectedSamples.size === 0}
            title="Export To CSV"
            onClick={() => void exportSamples('csv')}>
            <i className="ti ti-file-type-csv" /> Export To CSV
          </button>
          <span style={{ alignSelf: 'center', fontSize: 12, color: 'var(--ai-muted)' }}>
            {samples.length} sample{samples.length === 1 ? '' : 's'}
            {selectedSamples.size > 0 && ` (${selectedSamples.size} selected)`}
          </span>
        </div>

        {/* Samples grid — shared <AppDataGrid> (§9): selectable rows with
            Select All across all pages. Export buttons above export selected
            rows (or all rows when nothing is selected — ALPACA parity). */}
        <AppDataGrid<SampleRow>
          rows={samples}
          columns={GRID_COLS}
          getRowId={(r, i) => r.sampleLogInOid || i}
          emptyText="No data — pick a Category, Month and Year, then click Load."
          selectable
          selectedIds={selectedSamples}
          onSelectionChange={setSelectedSamples}
          getRowClassName={(r) => (r.isNew ? 'msr-row-new' : r.isModified ? 'msr-row-modified' : undefined)}
        />
      </>
    )
  );

  // ── Stepper + hint — driven by form state ──────────────────────────────────
  const periodSelected = !!category && month !== '' && !!year;
  const samplesLoaded  = samples.length > 0;

  const stepperSteps: IslandStep[] = viewOnly
    // A saved record is a completed snapshot — every step done (matches the
    // reference detail view).
    ? [
        { id: 'period', label: 'Select a Period', state: 'done' },
        { id: 'load',   label: 'Load',            state: 'done' },
        { id: 'save',   label: 'Save',            state: 'done' },
      ]
    : [
        { id: 'period', label: 'Select a Period', state: samplesLoaded ? 'done' : 'active' },
        { id: 'load',   label: 'Load',            state: samplesLoaded ? 'done' : periodSelected ? 'active' : 'todo' },
        { id: 'save',   label: 'Save',            state: samplesLoaded ? 'active' : 'todo' },
      ];

  const stepHint = viewOnly
    // Read-only snapshot — no Save/Edit; Delete removes it, Load reloads it.
    // Guidance (orange) tone = the app-wide standard hint colour (matches the
    // list + Custom Reporting), not green (user request 2026-07-15).
    ? <StepHint tone="guidance">
        <b>Viewing.</b> This saved Monthly Summary is read-only — use <b>Delete</b> to remove it, or <b>Load</b> to reload it from the server.
      </StepHint>
    : samplesLoaded
      ? <StepHint tone="guidance">
          <b>Step 3 · Save.</b> Click <b>Save</b> to store the Monthly Summary.
        </StepHint>
      : periodSelected
        ? <StepHint>
            <b>Step 2 · Load the samples.</b> Click <b>Load</b> to retrieve the monthly samples for the selected period.
          </StepHint>
        : <StepHint>
            <b>Step 1 · Select a period.</b> Choose Year, Month, and Category, then click <b>Load</b>.
          </StepHint>;

  const busy = loading || retrieving || deleting;

  // Keyboard shortcuts (standing rule 14) — registered once; each entry is gated
  // to fire only where its button actually renders (viewOnly / editing / new) and
  // mirrors that button's disabled state. Works in both inline and popup mode
  // (neither is aria-modal, so the shared store lets these through).
  useHotkeys([
    { combo: 'alt+l', label: 'Load', group: 'Monthly Summary Repository', order: 1, disabled: busy, run: () => void retrieve() },
    { combo: 'alt+shift+r', label: 'Reset', group: 'Monthly Summary Repository', order: 2, disabled: editing || busy, run: () => reset() },
    { combo: 'mod+s', label: 'Save', group: 'Monthly Summary Repository', order: 3, disabled: viewOnly || busy, run: () => void save() },
    { combo: 'alt+e', label: 'Edit', group: 'Monthly Summary Repository', order: 4, disabled: !(editing && viewOnly) || busy, run: () => setEditMode(true) },
    { combo: 'alt+d', label: 'Delete', group: 'Monthly Summary Repository', order: 5, disabled: !editing || busy, run: () => void deleteRecord() },
    { combo: 'alt+r', label: 'Refresh', group: 'Monthly Summary Repository', order: 6, disabled: !editing || busy, run: () => setRefreshKey((k) => k + 1) },
    { combo: 'alt+x', label: 'Export to', group: 'Monthly Summary Repository', order: 7, disabled: !editing || busy || samples.length === 0, run: () => void exportSamples('xlsx') },
    { combo: 'alt+n', label: 'New', group: 'Monthly Summary Repository', order: 8, disabled: editing || !onNew || busy, run: () => onNew?.() },
    { combo: 'esc', label: 'Cancel', group: 'Monthly Summary Repository', order: 9, allowInInput: false, run: () => onClose() },
  ]);

  // Heading action bar (top-right corner — matches the Custom Reporting detail
  // standard: primary action + Cancel). (user request 2026-07-15)
  //   • Viewing a saved MSR → READ-ONLY: Load (reload this record) · Cancel.
  //     No Reset/Save/Edit — a saved Monthly Summary is a snapshot; only Delete
  //     (sub-toolbar) can change it.
  //   • New MSR → Load (retrieve samples) · Reset · Save · Cancel.
  const actions = viewOnly ? (
    <>
      {/* Load pulls the live matching samples and MERGES them into what's
          already shown (mergeMsrRows) — ALPACA-LIMS Retrieve parity. A saved
          record starts from its persisted snapshot (possibly 0 rows if it was
          saved before this month's samples existed); Load discovers anything
          new without discarding what's already there. Reverted 2026-07-17: the
          2026-07-15 workaround routed this to setRefreshKey (reload the saved
          snapshot only) because retrieve() was silently returning 0 rows — the
          actual cause was a STRING_SPLIT/TRY_CONVERT trim bug in
          MonthlySummaryRepositoryReader.RetrieveSamplesAsync, fixed at the root. */}
      <button className="ca-btn ca-btn-solid" onClick={retrieve} disabled={busy}
        title={`Retrieve current matching samples from the server (merges with what's shown) (${formatCombo('alt+l')})`}>
        {retrieving ? <BusyIcon busy icon="ti-download" /> : <span aria-hidden="true">⬇️</span>} Load
      </button>
      <button className="ca-btn" onClick={onClose} title={`Back to the Monthly Summary Repository list (${formatCombo('esc')})`}>
        <span className="tb-i tb-i-grey">↩︎</span> Cancel
      </button>
    </>
  ) : editing ? (
    // Edit mode on an EXISTING saved record — Load runs the same merged
    // live retrieve as the New-record flow (see comment above); Save then
    // persists whatever is in the grid, including newly-discovered rows.
    <>
      <button className="ca-btn ca-btn-solid" onClick={retrieve} disabled={busy}
        title={`Retrieve current matching samples from the server (merges with what's shown) (${formatCombo('alt+l')})`}>
        {retrieving ? <BusyIcon busy icon="ti-download" /> : <span aria-hidden="true">⬇️</span>} Load
      </button>
      <button className="ca-btn ca-btn-solid" onClick={save} disabled={busy} title={`Save (${formatCombo('mod+s')})`}>
        <span className="tb-i">💾</span> Save
      </button>
      <button className="ca-btn" onClick={onClose} title={`Cancel (${formatCombo('esc')})`}>
        <span className="tb-i tb-i-grey">↩︎</span> Cancel
      </button>
    </>
  ) : (
    <>
      <button className="ca-btn ca-btn-solid" onClick={retrieve} disabled={busy} title={`Load (${formatCombo('alt+l')})`}>
        {retrieving ? <BusyIcon busy icon="ti-download" /> : <span aria-hidden="true">⬇️</span>} Load
      </button>
      <button className="ca-btn" onClick={reset} disabled={busy}
        title={`Clear the form (${formatCombo('alt+shift+r')})`}>
        <span className="tb-i tb-i-grey">↩︎</span> Reset
      </button>
      <button className="ca-btn ca-btn-solid" onClick={save} disabled={busy} title={`Save (${formatCombo('mod+s')})`}>
        <span className="tb-i">💾</span> Save
      </button>
      <button className="ca-btn" onClick={onClose} title={`Cancel (${formatCombo('esc')})`}>
        <span className="tb-i tb-i-grey">↩︎</span> Cancel
      </button>
    </>
  );

  // Sub-toolbar below the step-hint bar. A saved record shows the record-management
  // actions Edit · Delete · Refresh · Export to (user request 2026-07-15); a New
  // record shows New.
  const subToolbar = (
    <div style={{ display: 'flex', gap: 6, padding: '8px 0 4px', flexWrap: 'wrap' }}>
      {editing ? (
        <>
          {/* Edit — flips the read-only view into an editable form (Save appears
              in the corner); shown only while Viewing. */}
          {viewOnly && (
            <button className="ca-btn" onClick={() => setEditMode(true)} disabled={busy}
              title={`Edit this Monthly Summary (${formatCombo('alt+e')})`}>
              <span className="tb-i tb-i-blue">✎</span> Edit
            </button>
          )}
          <button className="ca-btn" onClick={deleteRecord} disabled={busy}
            title={`Delete this Monthly Summary (${formatCombo('alt+d')})`}>
            <span className="tb-i tb-i-red">🗑</span> Delete
          </button>
          <button className="ca-btn" onClick={() => setRefreshKey((k) => k + 1)} disabled={busy}
            title={`Reload this Monthly Summary from the server (${formatCombo('alt+r')})`}>
            {loading ? <BusyIcon busy icon="ti-refresh" /> : <span className="tb-i tb-i-green">🔄</span>} Refresh
          </button>
          <button className="ca-btn"
            onClick={() => void exportSamples('xlsx')}
            disabled={busy || samples.length === 0}
            title={`Export the samples to XLSX (${formatCombo('alt+x')})`}>
            <span className="tb-i tb-i-green">📤</span> Export to
          </button>
        </>
      ) : onNew ? (
        <button className="ca-btn" onClick={onNew} disabled={busy} title={`New Monthly Summary (${formatCombo('alt+n')})`}>
          <span className="tb-i tb-i-green">＋</span> New
        </button>
      ) : null}
      {/* Loaded + Audit chips at the END of this action row (after "Export to"),
          per the global rule; self-registers so <FormTitle> above drops them. */}
      <FormMetaChips />
    </div>
  );

  // ── Inline mode — full-page Settings-style detail (no popup) ────────────────
  if (inline) {
    return (
      <div className="bl-page mlv-host">
        <div className="bl-grid-area">
          {/* Island stepper — portalled into the top bar (BO UI Standard §5). */}
          <TopbarStepper steps={stepperSteps} />

          <div className="sd-wrap">
            {/* Header — FormTitle (icon + title + load-time chip + audit chip) on
                the LEFT; Load / Reset / Save / Cancel action buttons on the RIGHT.
                No separate sd-actionbar row — buttons live inline with the title
                (design prototype, user request 2026-07-15). */}
            <div className="sd-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                {/* FormTitle renders: nav icon · title · "Loaded in X sec" chip ·
                    Audit Trail chip — all from the shared components (§18.6). */}
                <FormTitle
                  pageId="msr"
                  title={recordName}
                  loadMs={loadMs}
                  loading={loading}
                  auditFormName="Monthly Summary Repository"
                />
                <div className="sd-bc">
                  {onHome && <><a onClick={onHome}>Home</a><i className="ti ti-chevron-right" /></>}
                  {group && <><span>{group}</span><i className="ti ti-chevron-right" /></>}
                  {title && <><a onClick={onClose}>{title}</a><i className="ti ti-chevron-right" /></>}
                  <span>{recordName}</span>
                </div>
              </div>
              {/* Action buttons — Load · Reset · Save · Cancel */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                {actions}
              </div>
            </div>

            {/* Contextual guidance strip — what to do next. */}
            {stepHint}

            {/* Secondary toolbar: New · Delete · Refresh (left-aligned, below step hint). */}
            {subToolbar}

            <div className="sd-body">{body}</div>
          </div>
        </div>
      </div>
    );
  }

  // ── Popup mode (legacy) — `.qs-overlay` modal ──────────────────────────────
  return (
    <div className="qs-overlay" onMouseDown={onClose}>
      <div className="qs-dialog" onMouseDown={(e) => e.stopPropagation()} style={{ width: 1100, maxWidth: '97vw' }}>
        <div className="qs-titlebar">
          <span>{recordName}</span>
          <div className="qs-titlebar-right">
            <i className="ti ti-x qs-close" title="Close" onClick={onClose} />
          </div>
        </div>
        <div className="qs-body" style={{ maxHeight: '78vh', overflow: 'auto' }}>{body}</div>
        <div className="qs-footer">
          <div className="qs-footer-actions">{actions}</div>
        </div>
      </div>
    </div>
  );
}
